import React from 'react'
import { useCallback, useEffect, useState } from "react";
import { MdLocationOn } from "react-icons/md";
import { PiVideoFill } from "react-icons/pi";
import { useParams } from "react-router-dom";
import { useTalentCbtDetailsQuery } from '../../reactWrapper/endpoints/talent/talent.api';
import axiosInstance from '../../reactWrapper/endpoints/axios';
import Badge from '../../components/Badge/Badge';
import DeepProfile from '../../components/Modal/DeepProfile';
import InterviewModal from '../../components/Modal/InterviewModal';
import OverallRating from '../../components/Rating/OverallRating';
import HrResults from '../../components/Rating/HrResult';
import TalentAvatar from '../../components/Cards/TalentAvatar';

export type BadgeInfo = {
  title: string;
  experience: string;
};

const IncompleteProfileDetails = () => {
  const { id } = useParams() as any;
  const { talentCbtDetails } = useTalentCbtDetailsQuery(id ?? "");  
  let verified = false;
  const [languages, setLanguages] = useState<BadgeInfo[]>([]);
  const [skills, setSkills] = useState<BadgeInfo[]>([]);
  const [stacks, setStacks] = useState<BadgeInfo[]>([]);
  const [frameworks, setFrameworks] = useState<BadgeInfo[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const fetchLanguages = useCallback(async () => {
    if (!talentCbtDetails || !talentCbtDetails?.userCbtLanguage) return;

    const data: BadgeInfo[] = [];
    const languageInfo = talentCbtDetails!.userCbtLanguage as any;
    delete languageInfo.user_id;
    delete languageInfo.id;
    delete languageInfo.created_at;
    delete languageInfo.updated_at;
    const length = Math.ceil(Object.keys(languageInfo).length / 2);
    const fetches = [];

    try {
      for (let idx = 1; idx <= length; idx++) {
        const id = languageInfo["language" + idx];
        if (id !== null && id !== undefined) {
          fetches.push(
            await axiosInstance.get(`/all/getCbtParamsRealname/${id}/language`)
          );
        }
      }

      await Promise.all(fetches).then((responses) => {
        responses.forEach((response, idx) => {
          data.push({
            title: response.data.data,
            experience: languageInfo["experience" + (idx + 1)],
          });
        });
      });
    } catch (error) {
      console.log(error);
    }

    setLanguages(data);
    return data;
  }, [talentCbtDetails]);

  const fetchSkills = useCallback(async () => {
    if (!talentCbtDetails || !talentCbtDetails.userCbtSkill) return;

    const data: BadgeInfo[] = [];
    const skillInfo = talentCbtDetails!.userCbtSkill as any;
    delete skillInfo.user_id;
    delete skillInfo.id;
    delete skillInfo.created_at;
    delete skillInfo.updated_at;
    const length = Math.ceil(Object.keys(skillInfo).length / 2);
    const fetches = [];

    try {
      for (let idx = 1; idx <= length; idx++) {
        const id = skillInfo["skill" + idx];
        if (id !== null && id !== undefined) {
          fetches.push(
            await axiosInstance.get(`/all/getCbtParamsRealname/${id}/skill`)
          );
        }
      }

      await Promise.all(fetches).then((responses) => {
        responses.forEach((response, idx) => {
          data.push({
            title: response.data.data,
            experience: skillInfo["experience" + (idx + 1)],
          });
        });
      });
    } catch (error) {
      console.log(error);
    }

    setSkills(data);
    return data;
  }, [talentCbtDetails]);

  const fetchStacks = useCallback(async () => {
    if (!talentCbtDetails || !talentCbtDetails.userCbtStack) return;

    const data: BadgeInfo[] = [];
    const stackInfo = talentCbtDetails!.userCbtStack as any;
    delete stackInfo.user_id;
    delete stackInfo.id;
    delete stackInfo.created_at;
    delete stackInfo.updated_at;
    const length = Math.ceil(Object.keys(stackInfo).length / 2);
    const fetches = [];

    try {
      for (let idx = 1; idx <= 1; idx++) {
        const id = stackInfo["stack" + idx];
        if (id !== null && id !== undefined) {
          fetches.push(
            await axiosInstance.get(`/all/getCbtParamsRealname/${id}/stack`)
          );
        }
      }

      await Promise.all(fetches).then((responses) => {
        responses.forEach((response, idx) => {
          data.push({
            title: response.data.data,
            experience: stackInfo["experience" + (idx + 1)],
          });
        });
      });
    } catch (error) {
      console.log(error);
    }

    setStacks(data);
    return data;
  }, [talentCbtDetails]);

  const fetchFrameworks = useCallback(async () => {
    if (!talentCbtDetails || !talentCbtDetails.userCbtFramework) return;

    const data: BadgeInfo[] = [];
    const frameworkInfo = talentCbtDetails!.userCbtFramework as any;
    delete frameworkInfo.user_id;
    delete frameworkInfo.id;
    delete frameworkInfo.created_at;
    delete frameworkInfo.updated_at;
    const length = Math.ceil(Object.keys(frameworkInfo).length / 2);
    const fetches = [];

    try {
      for (let idx = 1; idx <= length; idx++) {
        const id = frameworkInfo["framework" + idx];
        if (id !== null && id !== undefined) {
          fetches.push(
            await axiosInstance.get(`/all/getCbtParamsRealname/${id}/framework`)
          );
        }
      }

      Promise.all(fetches).then((responses) => {
        responses.forEach((response, idx) => {
          data.push({
            title: response.data.data,
            experience: frameworkInfo["experience" + (idx + 1)],
          });
        });
      });
    } catch (error) {
      console.log(error);
    }

    setFrameworks(data);
    return data;
  }, [talentCbtDetails]);

  useEffect(() => {
    if (!talentCbtDetails) return;

    Promise.all([
      fetchLanguages(),
      fetchSkills(),
      fetchStacks(),
      fetchFrameworks(),
    ]).then(([languages, skills, stacks, frameworks]) => {
      // setLanguages(languages ?? []);
      // setSkills(skills ?? []);
      // setStacks(stacks ?? []);
      // setFrameworks(frameworks ?? []);
    });
  }, [talentCbtDetails]);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* IDENTITY + ABOUT */}
      <section className="rounded-2xl border bg-card p-5 shadow-ui-sm">
        <TalentAvatar
          size={14}
          verified={verified}
          data={talentCbtDetails}
          edit={false}
        />

        <div className="mt-6 border-t pt-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-orange">
            About
          </div>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {talentCbtDetails?.short_bio ?? "Nil"}
          </p>
        </div>
      </section>

      {/* PROFILE + RATING */}
      <div className="grid gap-6 lg:grid-cols-3">
        <section className="space-y-6 rounded-2xl border bg-card p-5 shadow-ui-sm lg:col-span-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-lg bg-surface px-3 py-2">
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Location
                <MdLocationOn className="h-3.5 w-3.5 text-brand-orange" />
              </div>
              <p className="mt-1 text-sm font-semibold text-brand-navy">
                {[
                  talentCbtDetails?.userLocation?.state,
                  talentCbtDetails?.userLocation?.country,
                  talentCbtDetails?.userLocation?.continent,
                ]
                  .filter(Boolean)
                  .join(", ") || "Nil"}
              </p>
            </div>

            <div className="rounded-lg bg-surface px-3 py-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Experience
              </div>
              <p className="mt-1 text-sm font-semibold capitalize text-brand-navy">
                {talentCbtDetails?.userCbtLevel?.level_name ?? "Nil"} Level
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-sm font-bold text-brand-navy">Role</h2>
            <div className="mt-2 flex flex-wrap gap-3">
              {stacks?.map(({ title, experience }, idx) => (
                <Badge
                  label={title?.replace(/-/g, " ")}
                  period={experience}
                  key={idx}
                />
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-sm font-bold text-brand-navy">Specialties</h2>
            <div className="mt-2 flex flex-wrap gap-3">
              {languages?.map(({ title, experience }, idx) => (
                <Badge
                  label={title?.replace(/-/g, " ")}
                  period={experience}
                  key={idx}
                />
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-sm font-bold text-brand-navy">Tools</h2>
            <div className="mt-2 flex flex-wrap gap-3">
              {frameworks?.map(({ title, experience }, idx) => (
                <Badge
                  label={title?.replace(/-/g, " ")}
                  period={experience}
                  key={idx}
                />
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-sm font-bold text-brand-navy">Skills</h2>
            <div className="mt-2 flex flex-wrap gap-3">
              {skills?.map(({ title, experience }, idx) => (
                <Badge
                  label={title?.replace(/-/g, " ")}
                  period={experience}
                  key={idx}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border bg-card p-5 shadow-ui-sm">
          <OverallRating talentId={talentCbtDetails?.id} />
        </section>
      </div>

      {/* ASSESSMENT */}
      <section className="rounded-2xl border bg-card p-5 shadow-ui-sm">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-orange">
          Assessment
        </div>
        <h2 className="font-display text-lg font-semibold text-brand-navy">CBT Test Score</h2>

        <div className="mt-4 grid gap-6 sm:grid-cols-2">
          <div className="space-y-4">
            <div className="tabular-nums text-2xl font-bold text-brand-navy sm:text-3xl">
              {talentCbtDetails?.userCbtResult.length > 0
                ? talentCbtDetails?.userCbtResult[0].total_score
                : 0}
              %
            </div>
            <DeepProfile userId={id ?? ""} />
          </div>

          {talentCbtDetails?.userCbtSoftSkill?.interview_link && (
            <div className="flex flex-col items-start gap-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Interview
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(true)}
                aria-label="Play interview recording"
                className="grid h-16 w-24 place-items-center rounded-xl border border-dashed bg-surface transition-colors hover:border-brand-orange"
              >
                <PiVideoFill className="h-8 w-8 text-brand-orange" />
              </button>
              <InterviewModal
                open={isOpen}
                close={() => setIsOpen(false)}
                url={talentCbtDetails?.userCbtSoftSkill?.interview_link ?? ""}
              />
            </div>
          )}
        </div>
      </section>

      {/* HR RESULTS */}
      <HrResults userCbtSoftSkill={talentCbtDetails?.userCbtSoftSkill} />
    </div>
  );
};

export default IncompleteProfileDetails;